import { useEffect, useState, useCallback } from "react";
import { Toolbox, Plus, ShieldCheck, MapPin, Star, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Shared";
import { Avatar } from "@/components/Avatar";
import { useCurrency } from "@/context/CurrencyContext";
import { COUNTRIES, COUNTRY_CURRENCY } from "@/lib/countries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const emptyRequest = {
  title: "", discipline: "engineer", description: "", postal_code: "", city: "",
  country: "US", provider_id: "", needed_by: "",
};
const emptyProfile = {
  company: "", discipline: "engineer", discipline_other: "", postal_code: "", city: "",
  country: "US", registration_no: "", service_radius_km: 50, languages: "",
  hourly_rate: "", currency: "usd", callout_fee: "", bio: "", phone: "", remote_ok: true,
};

export default function Services() {
  const { user } = useAuth();
  const { currencies, base_currency: baseCurrency } = useCurrency();
  const isProvider = user?.role === "service_provider";
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("find");
  const [disciplines, setDisciplines] = useState([]);
  const [providers, setProviders] = useState([]);
  const [pending, setPending] = useState([]);
  const [requests, setRequests] = useState({ mine: [], assigned: [], available: [] });
  const [profile, setProfile] = useState(null);
  const [search, setSearch] = useState({ postal_code: "", country: "", discipline: "all" });
  const [dialog, setDialog] = useState(null);
  const [reqForm, setReqForm] = useState(emptyRequest);
  const [profileDraft, setProfileDraft] = useState(emptyProfile);
  const [quote, setQuote] = useState({ req: null, amount: "", currency: "usd", scope: "", days: 5 });
  const [review, setReview] = useState({ req: null, rating: 5, comment: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search.postal_code) params.set("postal_code", search.postal_code);
      if (search.country) params.set("country", search.country);
      if (search.discipline !== "all") params.set("discipline", search.discipline);
      const [p, r, mine] = await Promise.all([
        api.get(`/services/providers?${params.toString()}`),
        api.get("/services/requests"),
        isProvider || isAdmin ? api.get("/services/provider") : Promise.resolve({ data: null }),
      ]);
      setProviders(p.data); setRequests(r.data); setProfile(mine.data);
      if (isAdmin) {
        const { data } = await api.get("/services/providers?pending=true");
        setPending(data);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [search, isProvider, isAdmin]);

  useEffect(() => { api.get("/services/disciplines").then(({ data }) => setDisciplines(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const label = (key) => disciplines.find((d) => d.key === key)?.label || key;

  const payQuote = async (q) => {
    try {
      const { data } = await api.post(`/services/quotes/${q.id}/accept`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const TABS = [
    ["find", `Find a provider (${providers.length})`, MagnifyingGlass],
    ["requests", `My requests (${requests.mine.length})`, Toolbox],
    ...(isProvider || isAdmin ? [["desk", `Provider desk (${requests.assigned.length + requests.available.length})`, ShieldCheck]] : []),
    ...(isAdmin ? [["verify", `Verify (${pending.length})`, ShieldCheck]] : []),
  ];

  return (
    <div data-testid="services-page">
      <PageHeader
        overline="Professional services"
        title="Services near you"
        description="Accountants, engineers, surveyors, customs brokers, calibration labs and more — searched by postal or PIN code anywhere in the world."
        action={
          <div className="flex flex-wrap gap-2">
            {(isProvider || isAdmin) && (
              <Button variant="outline" data-testid="edit-practice-button" onClick={() => {
                setProfileDraft(profile ? { ...profile, languages: (profile.languages || []).join(", ") } : emptyProfile);
                setDialog("practice");
              }}>
                My practice
              </Button>
            )}
            <Button data-testid="raise-request-button" onClick={() => { setReqForm(emptyRequest); setDialog("request"); }}>
              <Plus size={16} className="mr-1.5" /> Request a service
            </Button>
          </div>
        }
      />

      {isProvider && profile && !profile.verified && (
        <div data-testid="provider-pending-banner" className="mb-4 border border-[#F6DCA0] bg-[#FFF8E6] px-4 py-3 text-sm text-[#8A6A00]">
          Your practice is waiting for admin verification — you will appear in the directory once it is approved.
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mb-4 min-w-0">
        <TabsList className="tab-strip no-scrollbar rounded-none bg-slate-100">
          {TABS.map(([value, text, Icon]) => (
            <TabsTrigger key={value} value={value} data-testid={`services-tab-${value}`} className="whitespace-nowrap rounded-none text-xs uppercase tracking-wider">
              <Icon size={14} className="mr-1.5" /> {text}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {tab === "find" && (
        <div data-testid="find-panel">
          <div className="mb-4 grid gap-2 sm:grid-cols-[200px_160px_1fr]">
            <Input data-testid="search-postal-input" placeholder="Postal / PIN code" value={search.postal_code} onChange={(e) => setSearch({ ...search, postal_code: e.target.value.toUpperCase() })} className="rounded-none border-slate-300" />
            <Input data-testid="search-country-input" placeholder="Country (IN, US…)" value={search.country} onChange={(e) => setSearch({ ...search, country: e.target.value.toUpperCase() })} className="rounded-none border-slate-300" />
            <Select value={search.discipline} onValueChange={(v) => setSearch({ ...search, discipline: v })}>
              <SelectTrigger data-testid="search-discipline-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="all">Every discipline</SelectItem>
                {disciplines.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {providers.length === 0 ? (
            <EmptyState testid="no-providers" title="No providers listed yet" description="Providers register their practice and postal code, an admin verifies them, and they show up here nearest first." />
          ) : (
            <div className="grid gap-3 lg:grid-cols-2" data-testid="provider-list">
              {providers.map((p) => (
                <div key={p.id} data-testid={`provider-${p.id}`} className="border border-border bg-white p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-2.5">
                      <Avatar imageId={p.logo_id || p.avatar_id} name={p.company} size="md" testid={`provider-avatar-${p.id}`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 text-sm font-semibold text-[#0F172A]">
                          {p.company}<ShieldCheck size={14} className="text-[#0B7A5B]" />
                        </div>
                        <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                          <span>{p.discipline_label}</span>
                          <span className="inline-flex items-center gap-1"><MapPin size={12} />{[p.postal_code, p.city, p.country].filter(Boolean).join(" · ")}</span>
                          {p.reviews ? (
                            <span className="inline-flex items-center gap-1"><Star size={12} weight="fill" className="text-[#F0A800]" />{p.rating} ({p.reviews})</span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    {(search.postal_code || search.country) && (
                      <span data-testid={`match-${p.id}`} className="border border-border px-1.5 py-0.5 text-[0.6rem] uppercase tracking-wider text-[#002FA7]">
                        {p.match_label}
                      </span>
                    )}
                  </div>
                  {p.bio && <p className="mt-2 line-clamp-3 text-xs text-slate-600">{p.bio}</p>}
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    {p.hourly_rate ? <span>{(p.currency || "usd").toUpperCase()} {Number(p.hourly_rate).toLocaleString()}/hr</span> : null}
                    {p.service_radius_km ? <span>· travels {p.service_radius_km} km</span> : null}
                    {p.user_id ? (
                      <Button size="sm" data-testid={`instruct-${p.id}`} onClick={() => {
                        setReqForm({ ...emptyRequest, provider_id: p.id, discipline: p.discipline, country: p.country, postal_code: p.postal_code });
                        setDialog("request");
                      }}>
                        Request a quote
                      </Button>
                    ) : (
                      <span data-testid={`listing-only-${p.id}`} className="italic">Directory listing — {p.phone || p.contact_name}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "requests" && (
        <div data-testid="requests-panel">
          {requests.mine.length === 0 ? (
            <EmptyState testid="no-requests" title="No service requests" description="Request a service and a verified provider in that discipline picks it up." />
          ) : (
            <div className="space-y-3">
              {requests.mine.map((r) => (
                <div key={r.id} data-testid={`request-${r.id}`} className="border border-border bg-white p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#0F172A]">{r.title}</div>
                      <div className="text-xs text-slate-500">
                        {r.reference} · {r.discipline_label} · {r.postal_code} {r.country} · {r.provider_name || "waiting for a provider"}
                      </div>
                    </div>
                    <StatusBadge value={r.status} testid={`request-status-${r.id}`} />
                  </div>
                  {(r.quotes || []).map((q) => (
                    <div key={q.id} data-testid={`quote-${q.id}`} className="mt-2 flex flex-wrap items-center justify-between gap-2 border border-border p-3 text-xs">
                      <span>{(q.currency || "usd").toUpperCase()} {Number(q.amount).toLocaleString()} — {q.scope} ({q.days} days)</span>
                      {["completed", "closed"].includes(r.status) ? <span className="text-slate-400">Job finished</span>
                        : q.payment_status === "paid" ? <span className="font-semibold text-[#0B7A5B]">Paid</span>
                        : q.status === "accepted" ? <span data-testid={`awaiting-payment-${q.id}`} className="font-semibold text-[#8A6A00]">Awaiting payment</span>
                        : <Button size="sm" data-testid={`pay-quote-${q.id}`} onClick={() => payQuote(q)}>Accept &amp; pay</Button>}
                    </div>
                  ))}
                  {r.status === "completed" && !r.rating && (
                    <Button size="sm" className="mt-2" data-testid={`review-${r.id}`} onClick={() => { setReview({ req: r, rating: 5, comment: "" }); setDialog("review"); }}>
                      Leave a review
                    </Button>
                  )}
                  {r.rating && <p className="mt-2 text-xs text-[#0B7A5B]" data-testid={`rating-${r.id}`}>You rated this {r.rating}/5</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "desk" && (isProvider || isAdmin) && (
        <div data-testid="desk-panel" className="space-y-6">
          <div>
            <div className="overline mb-2 text-slate-500">Open in my discipline and country</div>
            {requests.available.length === 0 ? (
              <EmptyState testid="no-available" title="Nothing waiting" description="New jobs in your discipline appear here, nearest postal codes first." />
            ) : (
              <div className="space-y-3">
                {requests.available.map((r) => (
                  <div key={r.id} data-testid={`available-${r.id}`} className="flex flex-wrap items-center justify-between gap-2 border border-border bg-white p-4">
                    <div>
                      <div className="text-sm font-semibold text-[#0F172A]">{r.title}</div>
                      <div className="text-xs text-slate-500">{r.reference} · {r.postal_code} {r.country} · {r.match_label}</div>
                    </div>
                    <Button size="sm" data-testid={`claim-${r.id}`} onClick={() => run(() => api.post(`/services/requests/${r.id}/claim`), "Job claimed")}>
                      Take it on
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="overline mb-2 text-slate-500">My jobs</div>
            {requests.assigned.length === 0 ? (
              <EmptyState testid="no-assigned" title="No jobs yet" description="Claim a job and it moves here." />
            ) : (
              <div className="space-y-3">
                {requests.assigned.map((r) => (
                  <div key={r.id} data-testid={`assigned-${r.id}`} className="border border-border bg-white p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#0F172A]">{r.title}</div>
                        <div className="text-xs text-slate-500">{r.reference} · {r.client_name} · {r.postal_code} {r.country}</div>
                      </div>
                      <StatusBadge value={r.status} testid={`assigned-status-${r.id}`} />
                    </div>
                    <p className="mt-2 whitespace-pre-line text-xs text-slate-600">{r.description}</p>
                    {(r.quotes || []).map((q) => (
                      <div key={q.id} data-testid={`desk-quote-${q.id}`} className="mt-2 flex flex-wrap items-center justify-between gap-2 border border-border p-2 text-xs">
                        <span>{(q.currency || "usd").toUpperCase()} {Number(q.amount).toLocaleString()} — {q.scope}</span>
                        <span className={q.payment_status === "paid" ? "font-semibold text-[#0B7A5B]" : "text-slate-500"}>
                          {q.payment_status === "paid" ? "Paid" : q.status === "accepted" ? "Accepted — payment pending" : "Awaiting the client"}
                        </span>
                      </div>
                    ))}
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" data-testid={`quote-btn-${r.id}`} disabled={["completed", "closed"].includes(r.status)} onClick={() => { setQuote({ req: r, amount: "", currency: "usd", scope: "", days: 5 }); setDialog("quote"); }}>
                        Quote a fee
                      </Button>
                      <Button size="sm" data-testid={`complete-${r.id}`} disabled={["completed", "closed"].includes(r.status)} onClick={() => run(() => api.post(`/services/requests/${r.id}/complete`), "Marked complete")}>
                        Mark complete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "verify" && isAdmin && (
        <div data-testid="verify-panel">
          {pending.length === 0 ? (
            <EmptyState testid="no-pending" title="Nothing to verify" description="New provider registrations land here." />
          ) : (
            <div className="space-y-3">
              {pending.map((p) => (
                <div key={p.id} data-testid={`pending-${p.id}`} className="border border-border bg-white p-4">
                  <div className="text-sm font-semibold text-[#0F172A]">{p.company}</div>
                  <div className="mt-1 text-xs text-slate-500">
                    {p.discipline_label} · {p.contact_name} · {p.email} · {p.postal_code} {p.city} {p.country} · reg {p.registration_no || "not given"}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" data-testid={`verify-${p.id}`} onClick={() => run(() => api.post(`/services/providers/${p.id}/verify`, { verified: true }), "Provider verified")}>Verify</Button>
                    <Button size="sm" variant="ghost" data-testid={`reject-${p.id}`} onClick={() => run(() => api.post(`/services/providers/${p.id}/verify`, { verified: false, note: "Declined" }), "Left unverified")}>Decline</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Request a service */}
      <Dialog open={dialog === "request"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Request a service</DialogTitle>
            <DialogDescription>Give the site postal or PIN code so the nearest provider can pick it up.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>What do you need</Label><Input data-testid="request-title-input" value={reqForm.title} onChange={(e) => setReqForm({ ...reqForm, title: e.target.value })} placeholder="Annual calibration of the CNC probes" className="rounded-none border-slate-300" /></div>
            <div>
              <Label>Discipline</Label>
              <Select value={reqForm.discipline} onValueChange={(v) => setReqForm({ ...reqForm, discipline: v })}>
                <SelectTrigger data-testid="request-discipline-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{disciplines.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Site postal / PIN code</Label><Input data-testid="request-postal-input" value={reqForm.postal_code} onChange={(e) => setReqForm({ ...reqForm, postal_code: e.target.value.toUpperCase() })} className="rounded-none border-slate-300" /></div>
            <div><Label>City</Label><Input data-testid="request-city-input" value={reqForm.city} onChange={(e) => setReqForm({ ...reqForm, city: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div>
              <Label>Country</Label>
              <Select value={reqForm.country} onValueChange={(v) => setReqForm({ ...reqForm, country: v })}>
                <SelectTrigger data-testid="request-country-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{COUNTRIES.map(([code, name]) => <SelectItem key={code} value={code}>{code} — {name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Needed by</Label><Input data-testid="request-date-input" type="date" value={reqForm.needed_by} onChange={(e) => setReqForm({ ...reqForm, needed_by: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div className="sm:col-span-2"><Label>The job</Label><Textarea data-testid="request-description-input" rows={5} value={reqForm.description} onChange={(e) => setReqForm({ ...reqForm, description: e.target.value })} className="rounded-none border-slate-300" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="submit-request-button" disabled={busy || !reqForm.title} onClick={() => run(async () => {
              await api.post("/services/requests", { ...reqForm, provider_id: reqForm.provider_id || null, needed_by: reqForm.needed_by || null });
              setDialog(null);
              setReqForm(emptyRequest);
            }, "Request raised")}>
              Raise request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quote */}
      <Dialog open={dialog === "quote"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Quote a fee</DialogTitle><DialogDescription>{quote.req?.reference}</DialogDescription></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Amount</Label><Input data-testid="quote-amount-input" type="number" value={quote.amount} onChange={(e) => setQuote({ ...quote, amount: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div>
              <Label>Currency</Label>
              <Select value={quote.currency} onValueChange={(v) => setQuote({ ...quote, currency: v })}>
                <SelectTrigger data-testid="quote-currency-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {Object.entries(currencies || {}).map(([code, c]) => (
                    <SelectItem key={code} value={code}>{code.toUpperCase()} — {c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Days</Label><Input data-testid="quote-days-input" type="number" value={quote.days} onChange={(e) => setQuote({ ...quote, days: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div className="sm:col-span-2"><Label>What the fee covers</Label><Textarea data-testid="quote-scope-input" rows={3} value={quote.scope} onChange={(e) => setQuote({ ...quote, scope: e.target.value })} className="rounded-none border-slate-300" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="submit-quote-button" disabled={busy || !quote.amount || !quote.scope} onClick={() => run(async () => {
              await api.post(`/services/requests/${quote.req.id}/quotes`, {
                amount: Number(quote.amount) || 0, currency: quote.currency,
                scope: quote.scope, days: Number(quote.days) || 5,
              });
              setDialog(null);
            }, "Fee sent")}>
              Send fee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review */}
      <Dialog open={dialog === "review"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rate the provider</DialogTitle><DialogDescription>{review.req?.provider_name}</DialogDescription></DialogHeader>
          <div className="flex gap-1.5" data-testid="rating-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" data-testid={`star-${n}`} onClick={() => setReview({ ...review, rating: n })} aria-label={`${n} stars`}>
                <Star size={26} weight={n <= review.rating ? "fill" : "regular"} className={n <= review.rating ? "text-[#F0A800]" : "text-slate-300"} />
              </button>
            ))}
          </div>
          <Textarea data-testid="review-comment-input" rows={3} value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} placeholder="How did the job go?" className="rounded-none border-slate-300" />
          <DialogFooter>
            <Button data-testid="submit-review-button" disabled={busy} onClick={() => run(async () => {
              await api.post(`/services/requests/${review.req.id}/review`, { rating: review.rating, comment: review.comment });
              setDialog(null);
            }, "Thanks for the review")}>
              Send review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Practice profile */}
      <Dialog open={dialog === "practice"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>My practice</DialogTitle>
            <DialogDescription>An admin verifies your registration before you appear in the directory.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Company / practice</Label><Input data-testid="practice-company-input" value={profileDraft.company} onChange={(e) => setProfileDraft({ ...profileDraft, company: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div>
              <Label>Discipline</Label>
              <Select value={profileDraft.discipline} onValueChange={(v) => setProfileDraft({ ...profileDraft, discipline: v })}>
                <SelectTrigger data-testid="practice-discipline-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{disciplines.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {profileDraft.discipline === "other" && (
              <div><Label>Describe your service</Label><Input data-testid="practice-other-input" value={profileDraft.discipline_other || ""} onChange={(e) => setProfileDraft({ ...profileDraft, discipline_other: e.target.value })} className="rounded-none border-slate-300" /></div>
            )}
            <div><Label>Postal / PIN code</Label><Input data-testid="practice-postal-input" value={profileDraft.postal_code} onChange={(e) => setProfileDraft({ ...profileDraft, postal_code: e.target.value.toUpperCase() })} className="rounded-none border-slate-300" /></div>
            <div><Label>City</Label><Input data-testid="practice-city-input" value={profileDraft.city || ""} onChange={(e) => setProfileDraft({ ...profileDraft, city: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div>
              <Label>Country</Label>
              <Select value={profileDraft.country} onValueChange={(v) => setProfileDraft({ ...profileDraft, country: v, currency: COUNTRY_CURRENCY[v] && currencies?.[COUNTRY_CURRENCY[v]] ? COUNTRY_CURRENCY[v] : profileDraft.currency })}>
                <SelectTrigger data-testid="practice-country-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{COUNTRIES.map(([code, name]) => <SelectItem key={code} value={code}>{code} — {name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Registration / licence no.</Label><Input data-testid="practice-reg-input" value={profileDraft.registration_no || ""} onChange={(e) => setProfileDraft({ ...profileDraft, registration_no: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Travels up to (km)</Label><Input data-testid="practice-radius-input" type="number" value={profileDraft.service_radius_km} onChange={(e) => setProfileDraft({ ...profileDraft, service_radius_km: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Hourly rate</Label><Input data-testid="practice-rate-input" type="number" value={profileDraft.hourly_rate} onChange={(e) => setProfileDraft({ ...profileDraft, hourly_rate: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Call-out fee</Label><Input data-testid="practice-callout-input" type="number" value={profileDraft.callout_fee} onChange={(e) => setProfileDraft({ ...profileDraft, callout_fee: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div>
              <Label>Currency</Label>
              <Select value={profileDraft.currency} onValueChange={(v) => setProfileDraft({ ...profileDraft, currency: v })}>
                <SelectTrigger data-testid="practice-currency-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {Object.entries(currencies || {}).map(([code, c]) => (
                    <SelectItem key={code} value={code}>{code.toUpperCase()} — {c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Languages</Label><Input data-testid="practice-languages-input" value={profileDraft.languages} onChange={(e) => setProfileDraft({ ...profileDraft, languages: e.target.value })} placeholder="English, Hindi" className="rounded-none border-slate-300" /></div>
            <div className="sm:col-span-2"><Label>About the practice</Label><Textarea data-testid="practice-bio-input" rows={4} value={profileDraft.bio || ""} onChange={(e) => setProfileDraft({ ...profileDraft, bio: e.target.value })} className="rounded-none border-slate-300" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="save-practice-button" disabled={busy || !profileDraft.company} onClick={() => run(async () => {
              await api.put("/services/provider", {
                ...profileDraft,
                languages: String(profileDraft.languages).split(",").map((s) => s.trim()).filter(Boolean),
                service_radius_km: Number(profileDraft.service_radius_km) || 0,
                hourly_rate: Number(profileDraft.hourly_rate) || 0,
                callout_fee: Number(profileDraft.callout_fee) || 0,
              });
              setDialog(null);
            }, "Practice saved")}>
              Save practice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
